import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Divider,
  Paper,
} from '@mui/material';
import {
  fetchUnits,
  fetchDevicesByUnit,
  verifyCitizen,
  clearVerifyResult,
  selectUnits,
  selectUnitsLoading,
  selectDevices,
  selectDevicesLoading,
  selectVerifyLoading,
  selectVerifyResult,
  selectError,
} from './slice';

function VerifyCitizenModal({ open, onClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const units = useSelector(selectUnits);
  const unitsLoading = useSelector(selectUnitsLoading);
  const devices = useSelector(selectDevices);
  const devicesLoading = useSelector(selectDevicesLoading);
  const verifyLoading = useSelector(selectVerifyLoading);
  const verifyResult = useSelector(selectVerifyResult);
  const error = useSelector(selectError);

  const [formData, setFormData] = useState({
    unitId: '',
    deviceId: '',
    maYeuCau: '',
    maDichVu: '',
    maCanBo: '',
    maDonVi: '',
    taiKhoan: '',
    soDienThoai: '',
    soDinhDanh: '',
    hoVaTen: '',
    nam: '',
    gioiTinh: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [generatedXml, setGeneratedXml] = useState('');
  const [xmlError, setXmlError] = useState('');

  // Fetch units when modal opens
  useEffect(() => {
    if (open) {
      dispatch(fetchUnits());
      // Reset form when opening
      setFormData({
        unitId: '',
        deviceId: '',
        maYeuCau: '',
        maDichVu: '',
        maCanBo: '',
        maDonVi: '',
        taiKhoan: '',
        soDienThoai: '',
        soDinhDanh: '',
        hoVaTen: '',
        nam: '',
        gioiTinh: '',
      });
      setFormErrors({});
      setGeneratedXml('');
      setXmlError('');
      dispatch(clearVerifyResult());
    }
  }, [open, dispatch]);

  // Fetch devices when unit changes
  useEffect(() => {
    if (formData.unitId) {
      dispatch(fetchDevicesByUnit({ unitId: formData.unitId }));
      setFormData((prev) => ({ ...prev, deviceId: '' }));
    }
  }, [formData.unitId, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.deviceId) errors.deviceId = t('lgspIntegration.errors.deviceRequired');
    if (!formData.maYeuCau) errors.maYeuCau = t('lgspIntegration.errors.maYeuCauRequired');
    if (!formData.maDichVu) errors.maDichVu = t('lgspIntegration.errors.maDichVuRequired');
    if (!formData.maCanBo) errors.maCanBo = t('lgspIntegration.errors.maCanBoRequired');
    if (!formData.soDinhDanh) errors.soDinhDanh = t('lgspIntegration.errors.soDinhDanhRequired');
    if (!formData.hoVaTen) errors.hoVaTen = t('lgspIntegration.errors.hoVaTenRequired');
    if (!formData.nam) errors.nam = t('lgspIntegration.errors.namRequired');
    if (!formData.gioiTinh) errors.gioiTinh = t('lgspIntegration.errors.gioiTinhRequired');

    // Validate year format
    if (formData.nam && !/^\d{4}$/.test(formData.nam)) {
      errors.nam = t('lgspIntegration.errors.namInvalid');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Generate XML from form data
  const generateXml = () => {
    if (!validateForm()) return;

    // Build optional XML elements
    const maDonViXml = formData.maDonVi ? `\n            <dan:MaDonVi>${formData.maDonVi}</dan:MaDonVi>` : '';
    const taiKhoanXml = formData.taiKhoan ? `\n            <dan:TaiKhoan>${formData.taiKhoan}</dan:TaiKhoan>` : '';
    const soDienThoaiXml = formData.soDienThoai ? `\n            <dan:SoDienThoai>${formData.soDienThoai}</dan:SoDienThoai>` : '';

    const xml = `<soapenv:Envelope
    xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
    xmlns:dan="http://dancuquocgia.bca">
    <soapenv:Header/>
    <soapenv:Body>
        <dan:XacThucThongTinCongDan
            xmlns:qldc="http://dancuquocgia.bca">
            <dan:MaYeuCau>${formData.maYeuCau}</dan:MaYeuCau>
            <dan:MaDichVu>${formData.maDichVu}</dan:MaDichVu>
            <dan:MaCanBo>${formData.maCanBo}</dan:MaCanBo>${maDonViXml}${taiKhoanXml}${soDienThoaiXml}
            <dan:SoDinhDanh>${formData.soDinhDanh}</dan:SoDinhDanh>
            <dan:HoVaTen>${formData.hoVaTen}</dan:HoVaTen>
            <dan:NgayThangNamSinh>
                <dan:Nam>${formData.nam}</dan:Nam>
            </dan:NgayThangNamSinh>
            <dan:GioiTinh>${formData.gioiTinh}</dan:GioiTinh>
        </dan:XacThucThongTinCongDan>
    </soapenv:Body>
</soapenv:Envelope>`;

    setGeneratedXml(xml);
    setXmlError('');
  };

  // Validate XML format
  const validateXml = (xmlString) => {
    if (!xmlString || !xmlString.trim()) {
      return false;
    }

    try {
      // Check for required XML structure elements
      const requiredElements = [
        '<soapenv:Envelope',
        '<soapenv:Body>',
        '<dan:XacThucThongTinCongDan',
        '<dan:MaYeuCau>',
        '<dan:MaDichVu>',
        '<dan:MaCanBo>',
        '<dan:SoDinhDanh>',
        '<dan:HoVaTen>',
        '<dan:NgayThangNamSinh>',
        '<dan:Nam>',
        '<dan:GioiTinh>',
        '</soapenv:Envelope>',
      ];

      for (const element of requiredElements) {
        if (!xmlString.includes(element)) {
          return false;
        }
      }

      // Try to parse XML using DOMParser
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
      const parseError = xmlDoc.getElementsByTagName('parsererror');
      if (parseError.length > 0) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  };

  // Handle XML textarea change
  const handleXmlChange = (e) => {
    setGeneratedXml(e.target.value);
    setXmlError('');
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    // Check if XML is empty
    if (!generatedXml || !generatedXml.trim()) {
      setXmlError(t('lgspIntegration.errors.xmlRequired'));
      return;
    }

    // Validate XML format if XML has been generated
    if (!validateXml(generatedXml)) {
      setXmlError(t('lgspIntegration.errors.invalidXmlFormat'));
      return;
    }

    const request = {
      deviceId: parseInt(formData.deviceId, 10),
      maYeuCau: formData.maYeuCau,
      maDichVu: formData.maDichVu,
      maCanBo: formData.maCanBo,
      maDonVi: formData.maDonVi || undefined,
      taiKhoan: formData.taiKhoan || undefined,
      soDienThoai: formData.soDienThoai || undefined,
      soDinhDanh: formData.soDinhDanh,
      hoVaTen: formData.hoVaTen,
      nam: formData.nam,
      gioiTinh: parseInt(formData.gioiTinh, 10),
    };

    dispatch(verifyCitizen(request));
  };

  const handleClose = () => {
    dispatch(clearVerifyResult());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('lgspIntegration.verifyTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {verifyResult && (
            <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'success.light' }}>
              <Typography variant="h6" color="success.dark" gutterBottom>
                {t('lgspIntegration.verifySuccess')}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                <strong>{t('lgspIntegration.result.code')}:</strong> {verifyResult.maKetQua}
              </Typography>
              <Typography variant="body2">
                <strong>{t('lgspIntegration.result.message')}:</strong> {verifyResult.moTaKetQua}
              </Typography>
              {verifyResult.data && (
                <>
                  <Typography variant="body2">
                    <strong>{t('lgspIntegration.result.idNumber')}:</strong> {verifyResult.data.soDinhDanh}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('lgspIntegration.result.fullName')}:</strong> {verifyResult.data.hoVaTen}
                  </Typography>
                </>
              )}
            </Paper>
          )}

          <Grid container spacing={2}>
            {/* Unit Selection */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!formErrors.unitId}>
                <InputLabel>{t('lgspIntegration.form.unit')}</InputLabel>
                <Select
                  name="unitId"
                  value={formData.unitId}
                  onChange={handleInputChange}
                  label={t('lgspIntegration.form.unit')}
                  disabled={unitsLoading}
                >
                  {units.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </MenuItem>
                  ))}
                </Select>
                {unitsLoading && <CircularProgress size={20} sx={{ position: 'absolute', right: 40, top: 18 }} />}
              </FormControl>
            </Grid>

            {/* Device Selection */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!formErrors.deviceId}>
                <InputLabel>{t('lgspIntegration.form.device')}</InputLabel>
                <Select
                  name="deviceId"
                  value={formData.deviceId}
                  onChange={handleInputChange}
                  label={t('lgspIntegration.form.device')}
                  disabled={!formData.unitId || devicesLoading}
                >
                  {devices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.deviceCode} - {device.name}
                    </MenuItem>
                  ))}
                </Select>
                {devicesLoading && <CircularProgress size={20} sx={{ position: 'absolute', right: 40, top: 18 }} />}
                {formErrors.deviceId && (
                  <Typography variant="caption" color="error">
                    {formErrors.deviceId}
                  </Typography>
                )}
                {formData.unitId && devices.length === 0 && !devicesLoading && (
                  <Typography variant="caption" color="warning.main">
                    {t('lgspIntegration.noDevicesWithAccess')}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('lgspIntegration.form.requestInfo')}
              </Typography>
            </Grid>

            {/* Request Info */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="maYeuCau"
                label={t('lgspIntegration.form.maYeuCau')}
                value={formData.maYeuCau}
                onChange={handleInputChange}
                error={!!formErrors.maYeuCau}
                helperText={formErrors.maYeuCau}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="maDichVu"
                label={t('lgspIntegration.form.maDichVu')}
                value={formData.maDichVu}
                onChange={handleInputChange}
                error={!!formErrors.maDichVu}
                helperText={formErrors.maDichVu}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="maCanBo"
                label={t('lgspIntegration.form.maCanBo')}
                value={formData.maCanBo}
                onChange={handleInputChange}
                error={!!formErrors.maCanBo}
                helperText={formErrors.maCanBo}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="maDonVi"
                label={t('lgspIntegration.form.maDonVi')}
                value={formData.maDonVi}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="taiKhoan"
                label={t('lgspIntegration.form.taiKhoan')}
                value={formData.taiKhoan}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="soDienThoai"
                label={t('lgspIntegration.form.soDienThoai')}
                value={formData.soDienThoai}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('lgspIntegration.form.citizenInfo')}
              </Typography>
            </Grid>

            {/* Citizen Info */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="soDinhDanh"
                label={t('lgspIntegration.form.soDinhDanh')}
                value={formData.soDinhDanh}
                onChange={handleInputChange}
                error={!!formErrors.soDinhDanh}
                helperText={formErrors.soDinhDanh}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="hoVaTen"
                label={t('lgspIntegration.form.hoVaTen')}
                value={formData.hoVaTen}
                onChange={handleInputChange}
                error={!!formErrors.hoVaTen}
                helperText={formErrors.hoVaTen || t('lgspIntegration.form.hoVaTenHint')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="nam"
                label={t('lgspIntegration.form.nam')}
                value={formData.nam}
                onChange={handleInputChange}
                error={!!formErrors.nam}
                helperText={formErrors.nam || t('lgspIntegration.form.namHint')}
                placeholder="1990"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!formErrors.gioiTinh} required>
                <InputLabel>{t('lgspIntegration.form.gioiTinh')}</InputLabel>
                <Select
                  name="gioiTinh"
                  value={formData.gioiTinh}
                  onChange={handleInputChange}
                  label={t('lgspIntegration.form.gioiTinh')}
                >
                  <MenuItem value={1}>{t('lgspIntegration.form.male')}</MenuItem>
                  <MenuItem value={2}>{t('lgspIntegration.form.female')}</MenuItem>
                </Select>
                {formErrors.gioiTinh && (
                  <Typography variant="caption" color="error">
                    {formErrors.gioiTinh}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Standardize Data Section */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('lgspIntegration.form.xmlData')}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={generateXml}
                  disabled={verifyLoading}
                  size="small"
                  id="standardizeData"
                  name="standardizeData"
                  data-testid="lgsp-button-standardizeData"
                >
                  {t('lgspIntegration.form.standardizeData')}
                </Button>
              </Box>
              {xmlError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {xmlError}
                </Alert>
              )}
              <TextField
                fullWidth
                multiline
                rows={12}
                name="generatedXml"
                value={generatedXml}
                onChange={handleXmlChange}
                placeholder={t('lgspIntegration.form.xmlPlaceholder')}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                  },
                }}
                id="generatedXml"
                data-testid="lgsp-textarea-generatedXml"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={verifyLoading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={verifyLoading || !formData.deviceId}
          startIcon={verifyLoading ? <CircularProgress size={20} /> : null}
        >
          {t('lgspIntegration.verifyButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default VerifyCitizenModal;
